import React, { useState, useEffect } from "react";
import AxiosInstance from "../../../AxiosInstance";
import { formatXaf, formatEurFromXaf } from "../../../utils/currency";

const PricingManagement = ({
    cities,
    selectedCity,
    setSelectedCity,
    showModal,
    showConfirmDialog,
    setTab,
    safeSetState,
    isMountedRef
}) => {
    const [services, setServices] = useState([]);
    const [selectedPricingService, setSelectedPricingService] = useState(null);
    const [editDialog, setEditDialog] = useState({
        visible: false,
        pricingId: null,
        price: "",
        basePrice: "",
        duration: "",
        pricingType: "fixed",
        hourlyRate: ""
    });

    // Charger les services selon les filtres
    useEffect(() => {
        if (selectedCity) {
            loadServices();
        }
    }, [selectedCity]);

    const loadServices = async () => {
        if (!isMountedRef.current) return;

        try {
            const response = await AxiosInstance.get(`/api/services/city/${selectedCity}`);
            if (isMountedRef.current) {
                const servicesData = response.data.services || response.data || [];
                safeSetState(setServices, Array.isArray(servicesData) ? servicesData : []);
            }
        } catch (error) {
            console.error("Erreur lors du chargement des services:", error);
            if (isMountedRef.current) {
                safeSetState(setServices, []);
            }
        }
    };

    const formatArrondissementsList = (arrondissements) => {
        if (!Array.isArray(arrondissements) || arrondissements.length === 0) {
            return "N/A";
        }

        const hasFullCity = arrondissements.find(arr => arr.id === 'full_city');
        if (hasFullCity) {
            return "Toute la ville";
        }

        return arrondissements.map(arr => arr.name).join(', ');
    };

    const editPricing = (pricingId) => {
        const service = services.find((s) => s.pricing && s.pricing._id === pricingId);
        if (service && service.pricing) {
            setEditDialog({
                visible: true,
                pricingId: pricingId,
                price: service.pricing.full_price ?? "0",
                basePrice: service.pricing.base_price ?? "0",
                duration: service.pricing.estimated_duration ?? "",
                pricingType: service.pricing.pricing_type ?? "fixed",
                hourlyRate: service.pricing.hourly_rate ?? ""
            });
        }
    };

    const handleUpdatePricing = async () => {
        const fullPrice = parseFloat(editDialog.price);
        const basePrice = parseFloat(editDialog.basePrice);
        const duration = parseInt(editDialog.duration);

        if (isNaN(fullPrice) || isNaN(basePrice)) {
            showModal("Veuillez entrer des prix valides.");
            return;
        }

        try {
            const updateData = {
                base_price: basePrice,
                full_price: fullPrice,
                pricing_type: editDialog.pricingType || "fixed"
            };

            if (!isNaN(duration)) {
                updateData.estimated_duration = duration;
            }

            if (editDialog.pricingType === 'hourly' && editDialog.hourlyRate) {
                updateData.hourly_rate = parseFloat(editDialog.hourlyRate);
            }

            await AxiosInstance.put(`/api/service-pricings/${editDialog.pricingId}`, updateData);
            showModal("Tarification modifiée avec succès !");
            setEditDialog({ ...editDialog, visible: false });
            loadServices();
        } catch (err) {
            console.error("Erreur lors de la modification :", err);
            showModal("Erreur lors de la modification de la tarification.");
        }
    };

    const deletePricing = (pricingId) => {
        showConfirmDialog(
            "Voulez-vous vraiment supprimer cette tarification ?",
            async () => {
                try {
                    await AxiosInstance.delete(`/api/service-pricings/${pricingId}`);
                    showModal("Tarification supprimée avec succès !");
                    loadServices();
                } catch (error) {
                    console.error("Erreur lors de la suppression:", error);
                    showModal("Erreur lors de la suppression de la tarification.");
                }
            }
        );
    };

    const startEditService = (service) => {
        // Rediriger vers le formulaire de service pour éditer
        setTab("create");
        // Les données du service seront chargées par le composant parent
    };

    return (
        <div className="tab-content active">
            <h2><strong className="label-black">Gestion des Prix par Localisation</strong></h2>

            <div className="pricing-filters">
                <div className="form-grid">
                    <div className="form-group">
                        <label>Sélectionner un Service</label>
                        <select
                            className="form-control"
                            value={selectedPricingService || ""}
                            onChange={(e) => setSelectedPricingService(e.target.value)}
                        >
                            <option value="">Choisir un service</option>
                            {Array.isArray(services) && services.map(s => (
                                <option key={s._id} value={s._id}>{s.title}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Filtrer par Ville</label>
                        <select
                            className="form-control"
                            value={selectedCity}
                            onChange={(e) => setSelectedCity(e.target.value)}
                        >
                            <option value="">Toutes les villes</option>
                            {Array.isArray(cities) && cities.map(c =>
                                <option key={c._id} value={c._id}>{c.name}</option>
                            )}
                        </select>
                    </div>
                </div>
            </div>

            {selectedPricingService ? (
                <div className="pricing-management">
                    <h3>Tarifications pour le service sélectionné</h3>
                    <div className="pricing-list">
                        {Array.isArray(services) && services
                            .filter(s => s._id === selectedPricingService)
                            .map(s => {
                                if (!s.pricing) {
                                    return (
                                        <div key={s._id} className="pricing-card no-pricing">
                                            <h4>{s.cityName} - {formatArrondissementsList(s.arrondissements)}</h4>
                                            <p className="no-pricing-text">Aucune tarification définie</p>
                                            <button
                                                className="btn btn-primary"
                                                onClick={() => startEditService(s)}
                                            >
                                                Définir une tarification
                                            </button>
                                        </div>
                                    );
                                }

                                return (
                                    <div className="pricing-card" key={`${s._id}-${s.locationId || 'default'}`}>
                                        <div className="pricing-header">
                                            <h4>{s.cityName} - {formatArrondissementsList(s.arrondissements)}</h4>
                                            <span className={`pricing-type type-${s.pricing.pricing_type}`}>
                                                {s.pricing.pricing_type}
                                            </span>
                                        </div>

                                        <div className="pricing-details">
                                            <div className="pricing-row">
                                                <span><strong>Prix de base:</strong></span>
                                                <span>{formatXaf(s.pricing.base_price)} <small>({formatEurFromXaf(s.pricing.base_price)} indicatif)</small></span>
                                            </div>
                                            <div className="pricing-row">
                                                <span><strong>Prix final:</strong></span>
                                                <span>{formatXaf(s.pricing.full_price)} <small>({formatEurFromXaf(s.pricing.full_price)} indicatif)</small></span>
                                            </div>
                                            {s.pricing.estimated_duration && (
                                                <div className="pricing-row">
                                                    <span><strong>Durée estimée:</strong></span>
                                                    <span>{s.pricing.estimated_duration} min</span>
                                                </div>
                                            )}
                                            {s.pricing.hourly_rate && (
                                                <div className="pricing-row">
                                                    <span><strong>Tarif horaire:</strong></span>
                                                    <span>{formatXaf(s.pricing.hourly_rate)}/h <small>({formatEurFromXaf(s.pricing.hourly_rate)}/h indicatif)</small></span>
                                                </div>
                                            )}
                                        </div>

                                        <div className="pricing-actions">
                                            <button
                                                className="btn btn-secondary"
                                                onClick={() => s.pricing && editPricing(s.pricing._id)}
                                            >
                                                ✏️ Modifier
                                            </button>
                                            <button
                                                className="btn btn-danger"
                                                onClick={() => deletePricing(s.pricing._id)}
                                            >
                                                🗑️ Supprimer
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                    </div>
                </div>
            ) : (
                <div className="pricing-placeholder">
                    <p>Sélectionnez un service pour voir ses tarifications.</p>
                </div>
            )}

            {/* Modal d'édition des prix */}
            {editDialog.visible && (
                <div className="custom-modal">
                    <div className="custom-modal-content large-modal">
                        <h3>Modifier la Tarification</h3>

                        <div className="form-grid">
                            <div className="form-group">
                                <label>Prix de base FCFA (XAF) *</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    value={editDialog.basePrice ?? ""}
                                    onChange={(e) => setEditDialog((prev) => ({ ...prev, basePrice: e.target.value }))}
                                />
                                {!isNaN(parseFloat(editDialog.basePrice)) && (
                                    <small style={{ color: "#666" }}>{formatEurFromXaf(editDialog.basePrice)} indicatif</small>
                                )}
                            </div>

                            <div className="form-group">
                                <label>Prix complet FCFA (XAF) *</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    value={editDialog.price ?? ""}
                                    onChange={(e) => setEditDialog((prev) => ({ ...prev, price: e.target.value }))}
                                />
                                {!isNaN(parseFloat(editDialog.price)) && (
                                    <small style={{ color: "#666" }}>{formatEurFromXaf(editDialog.price)} indicatif</small>
                                )}
                            </div>
                        </div>

                        <div className="form-grid">
                            <div className="form-group">
                                <label>Durée estimée (minutes)</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    value={editDialog.duration ?? ""}
                                    onChange={(e) => setEditDialog((prev) => ({ ...prev, duration: e.target.value }))}
                                />
                            </div>

                            <div className="form-group">
                                <label>Type de tarification</label>
                                <select
                                    className="form-control"
                                    value={editDialog.pricingType ?? "fixed"}
                                    onChange={(e) => setEditDialog((prev) => ({ ...prev, pricingType: e.target.value }))}
                                >
                                    <option value="fixed">Prix fixe</option>
                                    <option value="hourly">Par heure</option>
                                    <option value="per_unit">Par unité</option>
                                    <option value="per_m2">Par m²</option>
                                    <option value="negotiable">Négociable</option>
                                </select>
                            </div>
                        </div>

                        {editDialog.pricingType === 'hourly' && (
                            <div className="form-group">
                                <label>Tarif horaire FCFA (XAF)</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    value={editDialog.hourlyRate ?? ""}
                                    onChange={(e) => setEditDialog((prev) => ({ ...prev, hourlyRate: e.target.value }))}
                                />
                                {!isNaN(parseFloat(editDialog.hourlyRate)) && (
                                    <small style={{ color: "#666" }}>{formatEurFromXaf(editDialog.hourlyRate)}/h indicatif</small>
                                )}
                            </div>
                        )}

                        <div className="modal-actions">
                            <button
                                className="btn btn-secondary"
                                onClick={() => setEditDialog({ ...editDialog, visible: false })}
                            >
                                Annuler
                            </button>

                            <button
                                className="btn btn-primary"
                                onClick={handleUpdatePricing}
                            >
                                💾 Enregistrer
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PricingManagement;